<template>
    <Card padding="4" custom-classes="tw:overflow-visible">
        <div class="tw:flex tw:flex-col tw:gap-4">
            <h3 class="tw:text-sm tw:font-semibold tw:text-slate-600 tw:uppercase tw:tracking-wide tw:m-0">
                <i class="fas fa-sliders-h tw:mr-2 tw:text-blue-600"></i>Filtros de búsqueda y parametrización
            </h3>

            <!-- Búsqueda rápida + rango de fechas -->
            <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-2 tw:lg:grid-cols-4">
                <div class="tw:flex tw:flex-col tw:gap-1 tw:lg:col-span-2">
                    <label :class="labelClass" for="resumen-ventas-busqueda">Búsqueda rápida</label>
                    <SearchComponent
                        id="resumen-ventas-busqueda"
                        :value="filtros.search"
                        placeholder="Buscar por código o nombre de evento"
                        hide-label
                        data-cy="resumen-ventas-busqueda"
                        @input="cambiar('search', $event)"
                    />
                </div>

                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="resumen-ventas-del">Del</label>
                    <BaseDateInput
                        id="resumen-ventas-del"
                        :value="filtros.fecha_inicio || ''"
                        data-cy="resumen-ventas-del"
                        @input="cambiar('fecha_inicio', $event)"
                    />
                </div>

                <div class="tw:flex tw:flex-col tw:gap-1">
                    <label :class="labelClass" for="resumen-ventas-al">Al</label>
                    <BaseDateInput
                        id="resumen-ventas-al"
                        :value="filtros.fecha_fin || ''"
                        data-cy="resumen-ventas-al"
                        @input="cambiar('fecha_fin', $event)"
                    />
                </div>
            </div>

            <!-- Catálogos: los tres son `BaseMultiselect` (desplegable buscable),
                 que es el componente que usan los filtros del admin rediseñados. -->
            <div class="tw:grid tw:grid-cols-1 tw:gap-4 tw:md:grid-cols-3">
                <div class="tw:flex tw:flex-col tw:gap-1">
                    <span :class="labelClass">Buscar por teatro / recinto</span>
                    <BaseMultiselect
                        id="resumen-ventas-teatro"
                        name="resumen-ventas-teatro"
                        :value="filtros.search_teatro_id"
                        :options="teatros"
                        :loading="loading"
                        label-key="nombre"
                        placeholder="Todos los recintos"
                        data-cy="resumen-ventas-teatro"
                        @input="$emit('teatro-change', $event)"
                    />
                </div>

                <div class="tw:flex tw:flex-col tw:gap-1">
                    <span :class="labelClass">Buscar por taquillero / agente</span>
                    <BaseMultiselect
                        id="resumen-ventas-taquillero"
                        name="resumen-ventas-taquillero"
                        :value="filtros.taquillero_id"
                        :options="taquilleros"
                        :loading="loading"
                        label-key="nombre"
                        placeholder="Todos los agentes"
                        data-cy="resumen-ventas-taquillero"
                        @input="$emit('taquillero-change', $event)"
                    />
                </div>

                <div class="tw:flex tw:flex-col tw:gap-1">
                    <span :class="labelClass">Buscar por compañía</span>
                    <BaseMultiselect
                        id="resumen-ventas-compania"
                        name="resumen-ventas-compania"
                        :value="filtros.teatro_company_id"
                        :options="companias"
                        :loading="loading"
                        label-key="nombre"
                        placeholder="Todas las compañías productoras"
                        data-cy="resumen-ventas-compania"
                        @input="$emit('compania-change', $event)"
                    />
                </div>
            </div>

            <!-- Periodo + acciones -->
            <div class="tw:flex tw:flex-col tw:gap-3 tw:lg:flex-row tw:lg:items-center tw:lg:justify-between">
                <div class="tw:flex tw:flex-col tw:gap-3 tw:sm:flex-row tw:sm:flex-wrap tw:sm:items-center tw:sm:gap-6">
                    <ToggleSwitch
                        id="resumen-ventas-actuales"
                        data-cy="resumen-ventas-actuales"
                        variant="primary"
                        :value="!!filtros.actuales"
                        @input="$emit('periodo-change', { periodo: 'actuales', activo: $event })"
                    >
                        Eventos actuales
                    </ToggleSwitch>
                    <ToggleSwitch
                        id="resumen-ventas-pasados"
                        data-cy="resumen-ventas-pasados"
                        variant="secondary"
                        :value="!!filtros.pasados"
                        @input="$emit('periodo-change', { periodo: 'pasados', activo: $event })"
                    >
                        Eventos pasados
                    </ToggleSwitch>
                </div>

                <div class="tw:flex tw:flex-col tw:gap-2 tw:sm:flex-row tw:sm:justify-end">
                    <Button
                        variant="default"
                        icon="fas fa-trash"
                        custom-class="tw:justify-center"
                        data-cy="resumen-ventas-limpiar"
                        title="Limpiar filtros"
                        @click="$emit('limpiar')"
                    >
                        Limpiar filtros
                    </Button>
                    <Button
                        variant="default"
                        icon="fas fa-filter"
                        custom-class="tw:justify-center"
                        data-cy="resumen-ventas-buscar"
                        @click="$emit('buscar')"
                    >
                        Buscar
                    </Button>
                </div>
            </div>
        </div>
    </Card>
</template>

<script setup>
    import Card from '@/modules/admin/ui/Card.vue';
    import Button from '@/modules/admin/ui/Button.vue';
    import BaseDateInput from '@/modules/admin/ui/BaseDateInput.vue';
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue';
    import ToggleSwitch from '@/modules/admin/ui/ToggleSwitch.vue';
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue';

    const props = defineProps({
        /** Filtros vigentes. El componente NO los muta: emite parches. */
        filtros: {
            type: Object,
            required: true
        },
        teatros: {
            type: Array,
            default: () => []
        },
        taquilleros: {
            type: Array,
            default: () => []
        },
        companias: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    });

    const emit = defineEmits([
        'update:filtros',
        'buscar',
        'limpiar',
        'periodo-change',
        'teatro-change',
        'taquillero-change',
        'compania-change'
    ]);

    const labelClass = 'tw:block tw:text-xs tw:font-semibold tw:text-slate-500 tw:uppercase tw:tracking-wide';

    /**
     * Emite el cambio de un campo como parche, sin mutar la prop.
     *
     * @param {string} campo
     * @param {*} valor
     */
    function cambiar(campo, valor) {
        emit('update:filtros', { [campo]: valor });
    }
</script>
